import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import StewartInput from "controls/global/stewart-input";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";
import React, { useRef, useState } from "react";
import DecimalNumberFormat from "../../policy-management/shared/DecimalNumberFormat";

interface Props {
  name: keyof PremiumReceivablesReceiptRecord;
  disabled?: boolean;
  onChange: (name: keyof PremiumReceivablesReceiptRecord, value: string | null) => void;
  defaultValue?: string;
  // ReceiptAmount is a decimal on the backend (and can be negative — credits)
  // — masks the input to signed decimal entry only.
  numeric?: boolean;
  decimalScale?: number;
  // For whole-number ID fields (e.g. Agent ID) — strips non-digit characters
  // rather than reusing DecimalNumberFormat, which would add a thousands
  // separator (e.g. "12,345") that's wrong for an ID.
  integer?: boolean;
}

const InputSearch = ({
  name,
  disabled,
  onChange,
  defaultValue,
  numeric,
  decimalScale = 2,
  integer,
}: Props) => {
  const [value, setValue] = useState<string>(defaultValue ? defaultValue : "");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const clearInput = () => {
    // Update state/notify directly rather than mutating the DOM node and
    // replaying a synthetic event — for the numeric field, writing to
    // searchInputRef.current.value directly bypasses react-number-format's
    // own change detection and can leave its displayed value stale even
    // though the underlying filter was actually cleared.
    setValue("");
    onChange(name, "");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as keyof PremiumReceivablesReceiptRecord;
    const currentValue = integer
      ? event.target.value.replace(/\D/g, "")
      : event.target.value;
    if (value !== event.target.value.trim()) {
      onChange(fieldName, currentValue);
    }
    setValue(currentValue || "");
  };

  return (
    <StewartInput
      {...{ name }}
      id={name}
      onChange={handleInputChange}
      disabled={disabled}
      label="Search"
      inputRef={searchInputRef}
      InputProps={{
        endAdornment: !!value && (
          <IconButton onClick={clearInput} disabled={disabled}>
            <CloseIcon />
          </IconButton>
        ),
        ...(numeric ? { inputComponent: DecimalNumberFormat as any } : {}),
      }}
      {...(numeric ? { inputProps: { decimalScale, allowNegative: true } } : {})}
      {...(integer ? { inputProps: { inputMode: "numeric" } } : {})}
      InputLabelProps={{
        shrink: !!value,
      }}
      value={value}
    />
  );
};

export default InputSearch;
